import { type JSX, useState } from "react"

import { useTranslation } from "react-i18next"

import {
  AudioLines,
  BookOpen,
  Globe,
  Library,
  Orbit,
  ShieldCheck,
  Telescope,
  X,
} from "@/components/icons"
import { buttonVariants } from "@/components/ui/button-variants"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { M365Icon } from "@/features/ai-explorer/components/m365-icon"
import { cn } from "@/lib/utils"

function ToolsIcon({ className }: { className?: string }) {
  return (
    <svg
      aria-hidden="true"
      className={cn("size-4", className)}
      fill="none"
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
    >
      <line
        stroke="currentColor"
        strokeLinecap="round"
        strokeWidth="2"
        x1="4"
        x2="20"
        y1="8"
        y2="8"
      />
      <circle cx="9" cy="8" r="2" stroke="currentColor" strokeWidth="2" />
      <line
        stroke="currentColor"
        strokeLinecap="round"
        strokeWidth="2"
        x1="4"
        x2="20"
        y1="16"
        y2="16"
      />
      <circle cx="15" cy="16" r="2" stroke="currentColor" strokeWidth="2" />
    </svg>
  )
}

type ToolToggleItemProps = {
  icon: JSX.Element
  label: string
  enabled: boolean
  onToggle: (enabled: boolean) => void
  count?: number
}

function ToolToggleItem({ icon, label, enabled, onToggle, count }: ToolToggleItemProps) {
  return (
    <DropdownMenuItem
      aria-checked={enabled}
      role="menuitemcheckbox"
      className={cn(
        "my-0.5 gap-2",
        enabled && "text-accent-foreground dark:text-foreground dark:focus:text-accent-foreground"
      )}
      onSelect={(event) => {
        event.preventDefault()
        onToggle(!enabled)
      }}
    >
      {icon}
      <span className="min-w-0 flex-1 truncate">{label}</span>
      <span className="ml-auto flex items-center gap-2">
        {typeof count === "number" && count > 0 && (
          <span className="text-muted-foreground text-xs tabular-nums">({count})</span>
        )}
        <span className="flex size-4 items-center justify-center">
          <span
            className={cn(
              "size-2 rounded-full",
              enabled ? "bg-primary" : "border-muted-foreground/50 border"
            )}
          />
        </span>
      </span>
    </DropdownMenuItem>
  )
}

const MOCK_LIBRARIES = [
  { id: "hr", name: "HR Policies" },
  { id: "finance", name: "Finance Guidelines" },
  { id: "it", name: "IT & Security" },
]

const MOCK_TRUSTED_SOURCES = [{ id: "example", name: "Example Source" }]

export function DemoFooterToolbar() {
  const { t } = useTranslation()
  const [enableAgent, setEnableAgent] = useState(false)
  const [enableHighReasoning, setEnableHighReasoning] = useState(false)
  const [enableWebSearch, setEnableWebSearch] = useState(true)
  const [enableM365, setEnableM365] = useState(false)
  const [activeLibraryIds, setActiveLibraryIds] = useState<string[]>(["hr"])
  const [selectedTrustedSources, setSelectedTrustedSources] = useState<string[]>([])

  const iconClassName = (enabled: boolean) =>
    cn("size-4", enabled ? "text-primary" : "text-muted-foreground")

  const toggleLibrary = (id: string) => {
    setActiveLibraryIds((current) =>
      current.includes(id) ? current.filter((x) => x !== id) : [...current, id]
    )
  }

  const toggleTrustedSource = (id: string) => {
    setSelectedTrustedSources((current) =>
      current.includes(id) ? current.filter((x) => x !== id) : [...current, id]
    )
  }

  return (
    <div className="bg-card flex items-center gap-2 rounded-lg border p-3">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            aria-label={t("questioninput.tools", { defaultValue: "Tools" })}
            type="button"
            className={cn(
              buttonVariants({
                size: "md",
                variant: "ghost",
              }),
              "text-muted-foreground hover:text-foreground dark:hover:text-accent-foreground h-7 gap-1.5 px-2"
            )}
          >
            <ToolsIcon className="shrink-0" />
            <span>{t("questioninput.tools", { defaultValue: "Tools" })}</span>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="min-w-[220px]" side="top">
          {/* AI MODES SECTION */}
          <DropdownMenuLabel className="text-muted-foreground text-xs font-normal">
            {t("questioninput.modes_section", { defaultValue: "AI Mode" })}
          </DropdownMenuLabel>
          <ToolToggleItem
            enabled={enableAgent}
            icon={<Telescope className={iconClassName(enableAgent)} />}
            label={t("questioninput.agent_mode", { defaultValue: "Agent Mode" })}
            onToggle={setEnableAgent}
          />
          <ToolToggleItem
            enabled={enableHighReasoning}
            icon={<Orbit className={iconClassName(enableHighReasoning)} />}
            label={t("questioninput.high_reasoning", { defaultValue: "High Reasoning" })}
            onToggle={setEnableHighReasoning}
          />
          <DropdownMenuSeparator />

          {/* KNOWLEDGE SOURCES SECTION */}
          <DropdownMenuLabel className="text-muted-foreground text-xs font-normal">
            {t("questioninput.sources_section", { defaultValue: "Knowledge Sources" })}
          </DropdownMenuLabel>

          {/* Libraries submenu */}
          <DropdownMenuSub>
            <DropdownMenuSubTrigger>
              <Library
                className={cn(
                  "size-4",
                  activeLibraryIds.length > 0 ? "text-primary" : "text-muted-foreground"
                )}
              />
              <span className="flex min-w-0 flex-1 items-center justify-between gap-2">
                <span className="truncate">
                  {t("pages.common.libraries", { defaultValue: "Libraries" })}
                </span>
                {activeLibraryIds.length > 0 && (
                  <span className="text-muted-foreground shrink-0 text-xs tabular-nums">
                    ({activeLibraryIds.length})
                  </span>
                )}
              </span>
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent className="min-w-[200px]">
              {MOCK_LIBRARIES.map((lib) => (
                <DropdownMenuItem
                  key={lib.id}
                  className="gap-2"
                  onSelect={(e) => {
                    e.preventDefault()
                    toggleLibrary(lib.id)
                  }}
                >
                  <span className="min-w-0 flex-1 truncate">{lib.name}</span>
                  <span className="flex size-4 items-center justify-center">
                    <span
                      className={cn(
                        "size-2 rounded-full",
                        activeLibraryIds.includes(lib.id)
                          ? "bg-primary"
                          : "border-muted-foreground/50 border"
                      )}
                    />
                  </span>
                </DropdownMenuItem>
              ))}
            </DropdownMenuSubContent>
          </DropdownMenuSub>

          {/* Web Search toggle */}
          {!enableAgent && (
            <>
              <ToolToggleItem
                enabled={enableWebSearch}
                icon={<Globe className={iconClassName(enableWebSearch)} />}
                label={t("questioninput.web_search", { defaultValue: "Web Search" })}
                onToggle={setEnableWebSearch}
              />

              {/* Trusted Sources submenu (only when Web Search enabled) */}
              {enableWebSearch && (
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger>
                    <ShieldCheck
                      className={cn(
                        "size-4",
                        selectedTrustedSources.length > 0 ? "text-primary" : "text-muted-foreground"
                      )}
                    />
                    <span className="flex min-w-0 flex-1 items-center justify-between gap-2">
                      <span className="truncate">
                        {t("sources.trusted_sources", { defaultValue: "Trusted Sources" })}
                      </span>
                      {selectedTrustedSources.length > 0 && (
                        <span className="text-muted-foreground shrink-0 text-xs tabular-nums">
                          ({selectedTrustedSources.length})
                        </span>
                      )}
                    </span>
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent className="min-w-[180px]">
                    {MOCK_TRUSTED_SOURCES.map((source) => (
                      <DropdownMenuItem
                        key={source.id}
                        onSelect={(e) => {
                          e.preventDefault()
                          toggleTrustedSource(source.id)
                        }}
                      >
                        <span className="min-w-0 flex-1 truncate">{source.name}</span>
                        <span className="flex size-4 items-center justify-center">
                          <span
                            className={cn(
                              "size-2 rounded-full",
                              selectedTrustedSources.includes(source.id)
                                ? "bg-primary"
                                : "border-muted-foreground/50 border"
                            )}
                          />
                        </span>
                      </DropdownMenuItem>
                    ))}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem>
                      {t("sources.manage", { defaultValue: "Manage" })}
                    </DropdownMenuItem>
                  </DropdownMenuSubContent>
                </DropdownMenuSub>
              )}

              {/* M365 Search toggle */}
              <ToolToggleItem
                enabled={enableM365}
                icon={<M365Icon noText className={iconClassName(enableM365)} />}
                label={t("questioninput.m365_search", { defaultValue: "M365 Search" })}
                onToggle={setEnableM365}
              />
            </>
          )}
          <DropdownMenuSeparator />

          {/* UTILITIES SECTION */}
          <DropdownMenuLabel className="text-muted-foreground text-xs font-normal">
            {t("questioninput.utilities_section", { defaultValue: "Utilities" })}
          </DropdownMenuLabel>
          <DropdownMenuSub>
            <DropdownMenuSubTrigger>
              <BookOpen className="text-muted-foreground size-4" />
              <span className="min-w-0 flex-1 truncate">
                {t("prompts.title", { defaultValue: "Prompt Templates" })}
              </span>
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent className="min-w-[180px]">
              <DropdownMenuItem>
                {t("prompts.personal", { defaultValue: "Personal" })}
              </DropdownMenuItem>
              <DropdownMenuItem>
                {t("prompts.company", { defaultValue: "Company" })}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem>{t("prompts.manage", { defaultValue: "Manage" })}</DropdownMenuItem>
            </DropdownMenuSubContent>
          </DropdownMenuSub>
          <DropdownMenuItem className="gap-2">
            <AudioLines className="text-muted-foreground size-4" />
            <span className="min-w-0 flex-1 truncate">
              {t("questioninput.start_voice_mode", { defaultValue: "Voice Mode" })}
            </span>
            <span className="ml-auto size-4" />
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Agent Mode indicator pill - shown when agent mode is active */}
      {enableAgent && (
        <button
          aria-label={t("questioninput.disable_agent_mode", "Disable Agent Mode")}
          type="button"
          className={cn(
            "border-primary/30 bg-primary/10 text-primary hover:bg-primary/20 inline-flex h-7 items-center gap-1.5 rounded-full border px-2.5 text-xs font-medium transition-colors"
          )}
          onClick={() => setEnableAgent(false)}
        >
          <Telescope className="size-3.5" />
          <span>{t("questioninput.agent_mode", { defaultValue: "Agent Mode" })}</span>
          <X className="size-3 opacity-60" />
        </button>
      )}
    </div>
  )
}
